import { EntryModel } from "../models";
import { SportTypeEnum } from "../models/enums/SportTypeEnum";
import i18n from "../i18n";
import { apiClient } from "./apiClient";
import { parseServerDate } from "../utils/dateUtils";

// Response type from backend
interface EntryResponse {
  id: number;
  content: string;
  createdAt: string;
  editedAt?: string;
  userId: number;
  username: string;
  authorRole: string; // Author role for styling (e.g., ROOKIE entries shown in grey)
  matchId: number;
  homeTeam: string;
  awayTeam: string;
  matchDate: string;
  sportType: string;
  plusCount: number;
  minusCount: number;
  favCount: number;
  currentUserRating: string | null;
  currentUserHasFav: boolean;
  // Person entry fields — null for non-person entries
  personId: number | null;
  personFullName: string | null;
  personSlug: string | null;
  // Club entry fields — null for non-club entries
  clubId: number | null;
  clubName: string | null;
  clubSlug: string | null;
  // Team entry fields — null for non-team entries
  teamId: number | null;
  teamName: string | null;
  teamSlug: string | null;
}

/**
 * Post a new entry
 * Note: userId is NOT sent - backend extracts it from JWT token for security
 */
export const postNewEntry = async (
  content: string,
  matchId: number
): Promise<EntryModel> => {
  try {
    // Only send content and matchId - userId comes from JWT token on backend
    const responseJson = await apiClient.post<EntryResponse>("/api/entries", {
      content,
      matchId,
    });

    // Ensure createdAt is properly formatted
    const createdAt = responseJson.createdAt
      ? parseServerDate(responseJson.createdAt)
      : new Date();

    return {
      id: responseJson.id,
      content: responseJson.content,
      createdAt: createdAt,
      editedAt: responseJson.editedAt
        ? parseServerDate(responseJson.editedAt)
        : createdAt,
      userId: responseJson.userId,
      username: responseJson.username,
      authorRole: responseJson.authorRole || "ROOKIE", // Map author role for styling rookie entries
      matchId: responseJson.matchId,
      // Match info for display and URL generation
      homeTeam: responseJson.homeTeam || "",
      awayTeam: responseJson.awayTeam || "",
      matchDate: responseJson.matchDate ? new Date(responseJson.matchDate) : new Date(),
      sportType: responseJson.sportType as SportTypeEnum || SportTypeEnum.FOOTBALL,
      plusCount: responseJson.plusCount || 0,
      minusCount: responseJson.minusCount || 0,
      favCount: responseJson.favCount || 0,
      currentUserRating: responseJson.currentUserRating as any || null,
      currentUserHasFav: responseJson.currentUserHasFav || false,
      personId:          responseJson.personId ?? null,
      personFullName:    responseJson.personFullName ?? null,
      personSlug:        responseJson.personSlug ?? null,
      clubId:            responseJson.clubId ?? null,
      clubName:          responseJson.clubName ?? null,
      clubSlug:          responseJson.clubSlug ?? null,
      teamId:            responseJson.teamId ?? null,
      teamName:          responseJson.teamName ?? null,
      teamSlug:          responseJson.teamSlug ?? null,
    };
  } catch (error) {
    // Re-throw with translated message if it's a generic error
    if (error instanceof Error) {
      throw error;
    }
    throw new Error(i18n.t("error_post_entry_failed"));
  }
};